import { useEffect, useMemo, useRef, useState } from "react";
import { DocumentArrowUpIcon, DocumentTextIcon, MinusIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../../common/Button";
import Dropdown from "../../Settings/Dropdown";
import { startDocumentUpload } from "../../../services/agencyDocumentUploadService";
import { AgencyTagRow, DOC_TYPE_OPTIONS, formatBytes } from "./api";
import { TagChip } from "./tagColors";

const ACCEPT = ".pdf,.docx,.doc,.txt";
const MAX_MB: Record<string, number> = { pdf: 100, docx: 25, doc: 25, txt: 25 };

const ext = (name: string) => (name.split(".").pop() || "").toLowerCase();
const titleFrom = (name: string) => name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim();

interface Props {
  open: boolean;
  onClose: () => void;
  tags: AgencyTagRow[];
  // Files dropped on the page arrive pre-selected.
  initialFiles?: File[];
  onStarted?: () => void;
}

type DocType = "policy" | "statute" | "other";
const DEFAULT_TYPE: DocType = "policy";

// Optional pinned tags for one file: a tag pinned here is attached to every
// section of that document (as an admin tag, beside the auto tags), so use it
// only for a document that is entirely about one topic. Collapsed behind an
// "Pin tags" link so the common case stays a title and a type.
const FilePins: React.FC<{ tags: AgencyTagRow[]; pinned: string[]; onChange: (next: string[]) => void }> = ({ tags, pinned, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  // Focus the search once the panel has slid open (matches the 300 ms collapse).
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => searchRef.current?.focus(), 300);
    return () => window.clearTimeout(t);
  }, [open]);
  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tags.filter((t) => !pinned.includes(t.id) && (!q || t.name.toLowerCase().includes(q))).slice(0, 12);
  }, [tags, pinned, query]);

  return (
    <div className="px-4 pb-4 -mt-1">
      <div className="flex flex-wrap items-center gap-1.5">
        {pinned.map((id) => {
          const t = tags.find((x) => x.id === id);
          return t ? <TagChip key={id} name={t.name} color={t.color} onRemove={() => onChange(pinned.filter((x) => x !== id))} /> : null;
        })}
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-300 hover:underline"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {open ? <MinusIcon className="size-3.5" /> : <PlusIcon className="size-3.5" />}
          {pinned.length ? "Pin another tag" : "Pin tags to every section"}
        </button>
      </div>
      {/* Same grid-rows collapse as ConditionalFieldGroup: always mounted, slides open. */}
      <div
        className={cn(
          "grid transition-[grid-template-rows,opacity,margin] duration-300 ease-out",
          open ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0 mt-0 pointer-events-none"
        )}
        aria-hidden={!open}
      >
        <div className="overflow-hidden min-h-0">
        <div className="rounded-lg border border-base-300 bg-base-200/40 p-3">
          <p className="text-xs text-base-content/60">
            Sections are tagged automatically. Pin a tag only when the whole document is about one topic, like a stand-alone use of force directive; every section will carry it.
          </p>
          <input
            ref={searchRef}
            tabIndex={open ? 0 : -1}
            className="input input-bordered input-sm w-full mt-2 bg-base-100 focus:outline-none focus:border-blue-500"
            placeholder="Search tags…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {options.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5 max-h-24 overflow-auto">
              {options.map((t) => (
                <TagChip
                  key={t.id}
                  name={t.name}
                  color={t.color}
                  onClick={() => {
                    onChange([...pinned, t.id]);
                    setQuery("");
                  }}
                  className="opacity-80 hover:opacity-100"
                />
              ))}
            </div>
          ) : (
            <p className="mt-2 text-xs text-base-content/50">No matching tags.</p>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

// Any number of files, each with its own title, type, and optional pinned
// tags. The upload itself runs in the tray (services/agencyDocumentUploadService).
const UploadDocumentModal: React.FC<Props> = ({ open, onClose, tags, initialFiles, onStarted }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [titles, setTitles] = useState<Record<string, string>>({});
  const [docTypes, setDocTypes] = useState<Record<string, DocType>>({});
  // Pinned tags per file (keyed by file name, like titles and types).
  const [pins, setPins] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const next = initialFiles?.filter((f) => MAX_MB[ext(f.name)] != null) || [];
    setFiles(next);
    setTitles(Object.fromEntries(next.map((f) => [f.name, titleFrom(f.name)])));
    setDocTypes(Object.fromEntries(next.map((f) => [f.name, DEFAULT_TYPE])));
    setPins({});
    setError(null);
  }, [open, initialFiles]);

  const addFiles = (list: FileList | File[] | null) => {
    if (!list) return;
    const picked = Array.from(list);
    const rejected = picked.filter((f) => MAX_MB[ext(f.name)] == null);
    const tooBig = picked.filter((f) => MAX_MB[ext(f.name)] != null && f.size > MAX_MB[ext(f.name)] * 1024 * 1024);
    const ok = picked.filter((f) => MAX_MB[ext(f.name)] != null && !tooBig.includes(f));
    setError(
      rejected.length
        ? `Only PDF, Word (.docx/.doc) and plain-text files can be uploaded (skipped ${rejected.map((f) => f.name).join(", ")}).`
        : tooBig.length
          ? `${tooBig.map((f) => f.name).join(", ")} is larger than the ${MAX_MB[ext(tooBig[0].name)]} MB limit.`
          : null
    );
    setFiles((prev) => [...prev, ...ok.filter((f) => !prev.some((p) => p.name === f.name && p.size === f.size))]);
    setTitles((prev) => ({ ...prev, ...Object.fromEntries(ok.map((f) => [f.name, prev[f.name] || titleFrom(f.name)])) }));
    // New files take the current default type; files already in the list keep theirs.
    setDocTypes((prev) => ({ ...prev, ...Object.fromEntries(ok.map((f) => [f.name, prev[f.name] || DEFAULT_TYPE])) }));
  };


  const submit = () => {
    if (!files.length) return;
    for (const f of files) {
      startDocumentUpload({
        file: f,
        title: (titles[f.name] || titleFrom(f.name)).trim() || f.name,
        docType: docTypes[f.name] || DEFAULT_TYPE,
        presetTagIds: pins[f.name]?.length ? pins[f.name] : undefined,
        openPath: (id) => `/agency/dashboard/documents/${id}`,
      });
    }
    onStarted?.();
    onClose();
  };

  if (!open) return null;
  return (
    <div className="modal modal-open" role="dialog">
      <div className="modal-box max-w-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg">Upload documents</h3>
            <p className="text-sm text-base-content/60 mt-0.5">Policy manuals, directives, statutes, and ordinances. Each one is split into its own sections and tagged.</p>
          </div>
          <Button variant="clean" size="sm" className="px-1.5" onClick={onClose} title="Close">
            <XMarkIcon className="size-5" />
          </Button>
        </div>

        <div
          className={cn(
            "mt-5 rounded-xl border-2 border-dashed border-base-300 bg-base-200/40 text-center cursor-pointer hover:border-blue-400 transition-colors",
            files.length ? "px-4 py-3 flex items-center justify-center gap-3" : "px-4 py-8"
          )}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            addFiles(e.dataTransfer.files);
          }}
        >
          <DocumentArrowUpIcon className={cn("text-base-content/40", files.length ? "size-5" : "size-8 mx-auto")} />
          <div>
            <p className="text-sm font-medium text-base-content">{files.length ? "Add more files" : "Drop files here or click to choose"}</p>
            {!files.length && <p className="text-xs text-base-content/60 mt-1">PDF (scanned or native), Word, or plain text. Up to 100 MB per PDF.</p>}
          </div>
          <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
        </div>
        {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}

        {files.length > 0 && (
          <div className="mt-5">
            <p className="text-sm font-medium mb-2">{files.length === 1 ? "1 file" : `${files.length} files`}</p>
            <ul className="space-y-3">
              {files.map((f) => (
                <li key={`${f.name}-${f.size}`} className="rounded-xl border border-base-300 bg-base-100">
                  <div className="flex items-center gap-2 bg-gray-100 dark:bg-base-200 rounded-t-xl px-4 py-2">
                    <DocumentTextIcon className="size-5 shrink-0 text-base-content/40" />
                    <p className="text-sm text-base-content/80 truncate flex-1" title={f.name}>
                      {f.name} <span className="text-base-content/40">· {formatBytes(f.size)}</span>
                    </p>
                    <Button variant="clean" size="xs" className="px-1 -mr-1" title="Remove" onClick={() => setFiles((prev) => prev.filter((x) => x !== f))}>
                      <XMarkIcon className="size-4" />
                    </Button>
                  </div>
                  <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-xs font-medium text-base-content/60">Title</span>
                      <input
                        className="input input-bordered input-sm w-full mt-1 bg-base-100 focus:outline-none focus:border-blue-500"
                        value={titles[f.name] ?? ""}
                        onChange={(e) => setTitles((prev) => ({ ...prev, [f.name]: e.target.value }))}
                        placeholder="Title"
                        aria-label={`Title for ${f.name}`}
                      />
                    </label>
                    <div>
                      <span className="text-xs font-medium text-base-content/60">Type</span>
                      {/* The dropdown wrapper is inline-block and the sm trigger is
                          max-w-fit; both are overridden here so the trigger fills its
                          column and matches the input-sm height. */}
                      <div className="mt-1 [&_.dropdown]:block [&_.dropdown]:w-full">
                        <Dropdown
                          options={DOC_TYPE_OPTIONS}
                          value={docTypes[f.name] || DEFAULT_TYPE}
                          onChange={(v) => setDocTypes((prev) => ({ ...prev, [f.name]: v as DocType }))}
                          size="sm"
                          className="w-full !max-w-none !min-h-8 h-8 px-3"
                        />
                      </div>
                    </div>
                  </div>
                  <FilePins
                    tags={tags}
                    pinned={pins[f.name] || []}
                    onChange={(next) => setPins((prev) => ({ ...prev, [f.name]: next }))}
                  />
                </li>
              ))}
            </ul>
            <p className="text-xs text-base-content/60 mt-2">Statutes and ordinances are cited in reports in place of state statutes when they match. Policies drive the policy check and placeholders.</p>
          </div>
        )}

        <div className="modal-action">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!files.length} onClick={submit}>
            Upload {files.length > 1 ? `${files.length} documents` : "document"}
          </Button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose} />
    </div>
  );
};

export default UploadDocumentModal;
